import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { ConfirmDeletionModal } from '../../components/ConfirmDeletionModal';
import { useAuth } from '../../components/auth/AuthProvider';
import EventFormFields from '../../components/events/EventFormFields';
import { parseFeeConfig } from '../../components/events/eventFeeUtils';
import { useToast } from '../../components/toast/ToastContext';
import { deleteRegistration, downloadCertificate, getEventById, updateEvent, updateOfflinePayment, updateRegistrationAttendance } from '../../controllers/events';

function formatSessionTime(value) {
  if (!value) {
    return '-';
  }
  return String(value).slice(0, 5);
}

function mapSessionsFromEvent(event) {
  const sessions = event?.event_sessions || [];
  return sessions.map((session) => ({
    sessionDate: session.session_date || event?.starts_on || '',
    startTime: formatSessionTime(session.start_time) === '-' ? '' : formatSessionTime(session.start_time),
    endTime: formatSessionTime(session.end_time) === '-' ? '' : formatSessionTime(session.end_time),
    locationName: session.location_name || '',
    locationAddress: session.location_address || '',
    meetingUrl: session.meeting_url || '',
  }));
}

function mapEventToEditForm(event) {
  const mappedSessions = mapSessionsFromEvent(event);
  const sessionMode = mappedSessions.length > 1 ? 'multiple' : 'single';
  const paymentType = event?.payment_type === 'stripe' && parseInt(event?.visibility_level) !== 1
    ? 'offline'
    : (event?.payment_type || 'free');
  const baseFee = event?.fee_amount ?? '';
  const feeConfig = parseFeeConfig(event?.fee_config);
  const peopleByType = feeConfig?.peopleByType || {};
  const membershipByType = feeConfig?.membershipByType || {};
  const firstSession = mappedSessions[0] || {
    sessionDate: event?.starts_on || '',
    startTime: '19:00',
    endTime: '21:00',
    locationName: event?.location_name || '',
    locationAddress: event?.location_address || '',
    meetingUrl: event?.meeting_url || '',
  };

  return {
    title: event?.title || '',
    eventType: event?.event_type || '',
    description: event?.description || '',
    visibilityLevel: event?.visibility_level || 4,
    modality: event?.modality || 'presencial',
    locationName: event?.location_name || '',
    locationAddress: event?.location_address || '',
    meetingUrl: event?.meeting_url || '',
    cycleYear: event?.cycle_year || new Date().getFullYear(),
    startsOn: event?.starts_on || '',
    endsOn: event?.ends_on || '',
    sessionMode,
    singleSessionStartTime: firstSession.startTime || '19:00',
    singleSessionEndTime: firstSession.endTime || '21:00',
    registrationStatus: event?.registration_status || 'abierto',
    registrationCountMode: event?.registration_count_mode || 'membership',
    profileTypeFilter: event?.profile_type_filter ? String(event.profile_type_filter) : '',
    paymentType,
    feeAmount: event?.fee_amount || '',
    feePeopleAdult: peopleByType.adult ?? baseFee,
    feePeopleAdolescent: peopleByType.adolescent ?? baseFee,
    feePeopleChild: peopleByType.child ?? baseFee,
    feeMembershipMatrimonios: membershipByType.matrimonios ?? baseFee,
    feeMembershipMamas: membershipByType.mamas ?? baseFee,
    feeMembershipAsesorEspiritual: membershipByType.asesorEspiritual ?? baseFee,
    currency: 'USD',
    capacityLimit: event?.capacity_limit || '',
    certificateTemplate: event?.certificate_template || event?.event_type || 'default',
    sessions: sessionMode === 'multiple' ? mappedSessions : [],
  };
}

function toNullableNumber(value) {
  if (value === '' || value == null) {
    return null;
  }
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
}

function buildFeeConfig(form, paymentType) {
  if (paymentType === 'free') {
    return null;
  }

  if (form.registrationCountMode === 'people') {
    return {
      peopleByType: {
        adult: toNullableNumber(form.feePeopleAdult),
        adolescent: toNullableNumber(form.feePeopleAdolescent),
        child: toNullableNumber(form.feePeopleChild),
      },
    };
  }

  return {
    membershipByType: {
      matrimonios: toNullableNumber(form.feeMembershipMatrimonios),
      mamas: toNullableNumber(form.feeMembershipMamas),
      asesorEspiritual: toNullableNumber(form.feeMembershipAsesorEspiritual),
    },
  };
}

function hasAtLeastOnePrice(form, paymentType) {
  if (paymentType === 'free') {
    return true;
  }

  if (form.registrationCountMode === 'people') {
    return [form.feePeopleAdult, form.feePeopleAdolescent, form.feePeopleChild]
      .some((value) => value !== '' && value != null);
  }

  return [form.feeMembershipMatrimonios, form.feeMembershipMamas, form.feeMembershipAsesorEspiritual]
    .some((value) => value !== '' && value != null);
}

function EventsAdminDetail() {
  const { token } = useAuth();
  const showToast = useToast();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [savingEvent, setSavingEvent] = useState(false);
  const [error, setError] = useState('');
  const [editForm, setEditForm] = useState(null);
  const [registrationToDelete, setRegistrationToDelete] = useState(null);

  const sessions = useMemo(() => event?.event_sessions || [], [event]);
  const registrations = useMemo(() => event?.event_registrations || [], [event]);
  const effectiveEditForm = editForm || (event ? mapEventToEditForm(event) : null);

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  useEffect(() => {
    if (event) {
      setEditForm(mapEventToEditForm(event));
    }
  }, [event]);

  async function loadEvent() {
    setLoading(true);
    setError('');
    try {
      const data = await getEventById(token, eventId);
      setEvent(data);
    }
    catch (e) {
      setError(e.message || 'No se pudo cargar el evento.');
    }
    finally {
      setLoading(false);
    }
  }

  async function toggleAttendance(registration, attendance) {
    const key = `attendance-${registration.event_registration_id}-${attendance.event_session_id}`;
    setSavingKey(key);
    try {
      const updatedRegistration = await updateRegistrationAttendance(token, registration.event_registration_id, [{
        eventSessionId: attendance.event_session_id,
        attended: !attendance.attended,
      }]);

      setEvent((prev) => {
        if (!prev) {
          return prev;
        }

        const nextRegistrations = (prev.event_registrations || []).map((item) => {
          if (item.event_registration_id !== registration.event_registration_id) {
            return item;
          }

          return {
            ...item,
            ...(updatedRegistration || {}),
            membership: updatedRegistration?.membership || item.membership,
            event_attendances: updatedRegistration?.event_attendances || item.event_attendances,
          };
        });

        return {
          ...prev,
          event_registrations: nextRegistrations,
        };
      });

      showToast({
        message: 'Asistencia actualizada.',
        bg: 'success',
        autohide: true,
        delay: 5000,
        title: 'Asistencia'
      });
    }
    catch (e) {
      showToast({
        message: e.message || 'No se pudo actualizar asistencia.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error'
      });
    }
    finally {
      setSavingKey('');
    }
  }

  async function setOfflinePaymentStatus(registration, paymentStatus) {
    const key = `payment-${registration.event_registration_id}`;
    setSavingKey(key);
    try {
      const updatedRegistration = await updateOfflinePayment(token, registration.event_registration_id, { paymentStatus });

      setEvent((prev) => {
        if (!prev) {
          return prev;
        }

        const nextRegistrations = (prev.event_registrations || []).map((item) => {
          if (item.event_registration_id !== registration.event_registration_id) {
            return item;
          }

          return {
            ...item,
            ...(updatedRegistration || {}),
            membership: updatedRegistration?.membership || item.membership,
            event_attendances: updatedRegistration?.event_attendances || item.event_attendances,
          };
        });

        return {
          ...prev,
          event_registrations: nextRegistrations,
        };
      });

      showToast({
        message: 'Estado de pago actualizado.',
        bg: 'success',
        autohide: true,
        delay: 5000,
        title: 'Pago'
      });
    }
    catch (e) {
      showToast({
        message: e.message || 'No se pudo actualizar pago manual.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error'
      });
    }
    finally {
      setSavingKey('');
    }
  }

  async function onDownloadCertificate(registration) {
    const key = `certificate-${registration.event_registration_id}`;
    setSavingKey(key);
    try {
      await downloadCertificate(token, registration.event_registration_id);
      showToast({
        message: 'Certificado generado/descargado.',
        bg: 'success',
        autohide: true,
        delay: 5000,
        title: 'Certificado'
      });
    }
    catch (e) {
      showToast({
        message: e.message || 'No se pudo descargar certificado.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error'
      });
    }
    finally {
      setSavingKey('');
    }
  }

  function canDownloadCertificate(registration) {
    const totalSessions = event?.event_sessions?.length || 0;
    const attendedCount = (registration?.event_attendances || []).filter((attendance) => attendance.attended).length;
    return totalSessions > 0 && attendedCount === totalSessions;
  }

  function canDeleteRegistration(registration) {
    if (!registration) {
      return false;
    }

    const hasAttendanceMarked = (registration.event_attendances || []).some((attendance) => attendance.attended);

    if (hasAttendanceMarked) {
      return false;
    }

    return event?.payment_type === 'free' || registration.payment_status !== 'pagado';
  }

  function updateField(field, value) {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  }

  function requestDeleteRegistration(registration) {
    setRegistrationToDelete(registration);
  }

  async function confirmDeleteRegistration() {
    if (!registrationToDelete) {
      return;
    }

    try {
      await deleteRegistration(token, registrationToDelete.event_registration_id);
      setEvent((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          event_registrations: (prev.event_registrations || []).filter((item) => item.event_registration_id !== registrationToDelete.event_registration_id),
        };
      });

      showToast({
        message: 'Registro eliminado correctamente.',
        bg: 'success',
        autohide: true,
        delay: 5000,
        title: 'Registro eliminado'
      });
      setRegistrationToDelete(null);
    }
    catch (e) {
      showToast({
        message: e.message || 'No se pudo eliminar el registro.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error'
      });
    }
  }

  function updateSession(index, key, value) {
    setEditForm((prev) => {
      const sessions = [...(prev?.sessions || [])];
      sessions[index] = { ...sessions[index], [key]: value };
      return { ...prev, sessions };
    });
  }

  function updateSessionMode(mode) {
    setEditForm((prev) => {
      if (!prev) {
        return prev;
      }
      if (mode === 'single') {
        return { ...prev, sessionMode: mode, sessions: [] };
      }
      if (!prev.sessions?.length) {
        return {
          ...prev,
          sessionMode: mode,
          sessions: [{
            sessionDate: prev.startsOn,
            startTime: '19:00',
            endTime: '21:00',
            locationName: prev.locationName || '',
            locationAddress: prev.locationAddress || '',
            meetingUrl: prev.meetingUrl || '',
          }],
        };
      }
      return { ...prev, sessionMode: mode };
    });
  }

  function normalizePaymentTypeForVisibility(paymentType, visibilityLevel) {
    if (parseInt(visibilityLevel) !== 1 && paymentType === 'stripe') {
      return 'offline';
    }

    return paymentType;
  }

  function addSession() {
    setEditForm((prev) => {
      if (!prev || prev.sessionMode === 'single') {
        return prev;
      }
      return {
        ...prev,
        sessions: [...prev.sessions, {
          sessionDate: prev.endsOn,
          startTime: '19:00',
          endTime: '21:00',
          locationName: prev.locationName || '',
          locationAddress: prev.locationAddress || '',
          meetingUrl: prev.meetingUrl || '',
        }],
      };
    });
  }

  function removeSession(index) {
    setEditForm((prev) => {
      if (!prev || prev.sessions.length <= 1) {
        return prev;
      }
      return {
        ...prev,
        sessions: prev.sessions.filter((_, i) => i !== index),
      };
    });
  }

  async function saveEventChanges(e) {
    e.preventDefault();
    if (!event || !editForm) {
      return;
    }

    setSavingEvent(true);

    try {
      const effectivePaymentType = normalizePaymentTypeForVisibility(editForm.paymentType, event.visibility_level);
      if (!hasAtLeastOnePrice(editForm, effectivePaymentType)) {
        throw new Error('Define al menos un precio para el tipo de cupo seleccionado.');
      }

      const feeConfig = buildFeeConfig(editForm, effectivePaymentType);
      const payload = {
        ...editForm,
        visibilityLevel: event.visibility_level,
        regionId: event.region_id,
        diocesisId: event.diocesis_id,
        comunidadId: event.comunidad_id,
        profileTypeFilter: editForm.profileTypeFilter ? parseInt(editForm.profileTypeFilter) : null,
        paymentType: effectivePaymentType,
        feeConfig,
        currency: 'USD',
        singleSessionStartTime: editForm.sessionMode === 'single' ? editForm.singleSessionStartTime : undefined,
        singleSessionEndTime: editForm.sessionMode === 'single' ? editForm.singleSessionEndTime : undefined,
        sessions: editForm.sessionMode === 'single' ? [{
          sessionDate: editForm.startsOn,
          startTime: editForm.singleSessionStartTime,
          endTime: editForm.singleSessionEndTime,
          meetingUrl: editForm.meetingUrl || '',
          locationName: editForm.locationName || '',
          locationAddress: editForm.locationAddress || '',
        }] : editForm.sessions,
        feeAmount: effectivePaymentType === 'free' ? 0 : undefined,
        capacityLimit: editForm.capacityLimit === '' ? null : parseInt(editForm.capacityLimit),
      };

      await updateEvent(token, eventId, payload);
      showToast({
        message: 'Evento actualizado correctamente.',
        bg: 'success',
        autohide: true,
        delay: 5000,
        title: 'Evento actualizado'
      });
      await loadEvent();
    }
    catch (e2) {
      showToast({
        message: e2.message || 'No se pudo actualizar el evento.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error'
      });
    }
    finally {
      setSavingEvent(false);
    }
  }

  return (
    <Container className="py-4">
      <Row className="mb-3">
        <Col>
          <Link to="/app/admin/eventos">Regresar a eventos</Link>
          <h1 className="mt-2 mb-1">Detalle de evento</h1>
          {event && <p className="text-muted mb-0">{event.title} · {event.event_type}</p>}
        </Col>
      </Row>

      {!!error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : (
        <>
          <Card className="mb-4">
            <Card.Header><strong>Editar evento</strong></Card.Header>
            <Card.Body>
              <Form onSubmit={saveEventChanges}>
                <Row className="g-3 mb-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Nivel</Form.Label>
                      <Form.Control
                        value={
                          event?.visibility_level === 1 ? 'Nacional'
                            : event?.visibility_level === 2 ? 'Regional'
                              : event?.visibility_level === 3 ? 'Diócesis'
                                : 'Comunidad'
                        }
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  {event?.visibility_level >= 2 && (
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Región</Form.Label>
                        <Form.Control value={event?.region?.label || '-'} readOnly disabled />
                      </Form.Group>
                    </Col>
                  )}
                  {event?.visibility_level >= 3 && (
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Diócesis</Form.Label>
                        <Form.Control value={event?.diocesis?.label || '-'} readOnly disabled />
                      </Form.Group>
                    </Col>
                  )}
                  {event?.visibility_level >= 4 && (
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Comunidad</Form.Label>
                        <Form.Control value={event?.comunidad?.label || '-'} readOnly disabled />
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <EventFormFields
                  form={effectiveEditForm}
                  fixedEventType={event?.event_type}
                  fixedVisibilityLevel={event?.visibility_level}
                  fixedRegionId={event?.region_id}
                  fixedDiocesisId={event?.diocesis_id}
                  fixedComunidadId={event?.comunidad_id}
                  onFieldChange={updateField}
                  onSessionModeChange={updateSessionMode}
                  onAddSession={addSession}
                  onRemoveSession={removeSession}
                  onSessionChange={updateSession}
                  readOnlyType
                />
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    disabled={savingEvent}
                    onClick={() => setEditForm(mapEventToEditForm(event))}
                  >
                    Revertir
                  </Button>
                  <Button type="submit" disabled={savingEvent}>{savingEvent ? 'Guardando...' : 'Guardar cambios'}</Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Inscripciones</strong>
              <Badge bg="secondary">{registrations.length}</Badge>
            </Card.Header>
            <Card.Body style={{ overflowX: 'auto' }}>
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Membresia</th>
                    <th>Miembros</th>
                    <th>Estado</th>
                    <th>Pago</th>
                    <th>Personas</th>
                    <th>Asistencia</th>
                    <th>Certificado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((registration) => {
                    const fullName = registration.membership?.label || registration.membership?.user?.email;

                    return (
                      <tr key={`reg-row-${registration.event_registration_id}`}>
                        <td>{registration.membership?.membership_id}</td>
                        <td>{fullName}</td>
                        <td>{registration.registration_status}</td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <span>{registration.payment_status}</span>
                            {event?.payment_type === 'offline' && (
                              <Form.Select
                                size="sm"
                                value={registration.payment_status}
                                disabled={savingKey === `payment-${registration.event_registration_id}`}
                                onChange={e => setOfflinePaymentStatus(registration, e.target.value)}
                              >
                                <option value="por_pagar">Por pagar</option>
                                <option value="pagado">Pagado</option>
                                <option value="reembolsado">Reembolsado</option>
                              </Form.Select>
                            )}
                          </div>
                        </td>
                        <td>{registration.people_count}</td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {(registration.event_attendances || []).map((attendance) => (
                              <Button
                                key={`attendance-${registration.event_registration_id}-${attendance.event_session_id}`}
                                size="sm"
                                variant={attendance.attended ? 'success' : 'outline-secondary'}
                                disabled={savingKey === `attendance-${registration.event_registration_id}-${attendance.event_session_id}`}
                                onClick={() => toggleAttendance(registration, attendance)}
                              >
                                Sesion {attendance.event_session_id}: {attendance.attended ? 'Asistio' : 'No asistio'}
                              </Button>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {canDownloadCertificate(registration) ? (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                disabled={savingKey === `certificate-${registration.event_registration_id}`}
                                onClick={() => onDownloadCertificate(registration)}
                              >
                                PDF
                              </Button>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {canDeleteRegistration(registration) ? (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => requestDeleteRegistration(registration)}
                              >
                                Eliminar
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!registrations.length && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted">No hay inscripciones en este evento.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <ConfirmDeletionModal
            show={!!registrationToDelete}
            handleClose={() => setRegistrationToDelete(null)}
            title="Eliminar registro"
            triggerDelete={confirmDeleteRegistration}
            body={registrationToDelete ? (
              <>
                <p>Vas a eliminar el registro de <strong>{registrationToDelete.membership?.membership_id}</strong>.</p>
                <p>Solo se permite si el evento es gratis o si el pago todavía no se ha completado, y si no tiene asistencias marcadas.</p>
                <p>Esta acción no podrá deshacerse.</p>
              </>
            ) : null}
          />
        </>
      )}
    </Container>
  );
}

export default EventsAdminDetail;
